import React, { useState } from 'react'
import {
    Box,
    Collapse,
    IconButton,
    Typography,
    useTheme,
} from '@mui/material'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'

import { InspectionChipStatus, InspectionStatusChip } from './InspectionStatusChip'

interface LifecycleSectionProps {
    sectionNumber: number
    title: string
    icon: React.ReactNode
    status?: InspectionChipStatus
    statusLabel?: string
    summary?: string
    defaultExpanded?: boolean
    children: React.ReactNode
}

export const LifecycleSection: React.FC<LifecycleSectionProps> = ({
    sectionNumber,
    title,
    icon,
    status,
    statusLabel,
    summary,
    defaultExpanded = false,
    children,
}) => {
    const theme = useTheme()
    const [expanded, setExpanded] = useState(defaultExpanded)

    return (
        <Box
            sx={{
                bgcolor: 'rgba(138, 142, 140, 0.16)',
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.1)',
                overflow: 'hidden',
                width: '100%',
            }}
        >
            <Box
                onClick={() => setExpanded(!expanded)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 2,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                }}
            >
                <Box
                    sx={{
                        color: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    {icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="overline"
                        sx={{
                            color: 'rgba(255,255,255,0.45)',
                            letterSpacing: 1,
                            display: 'block',
                            lineHeight: 1.2,
                        }}
                    >
                        {sectionNumber}. {title}
                    </Typography>
                    {!expanded && summary && (
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'rgba(255,255,255,0.55)',
                                mt: 0.25,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {summary}
                        </Typography>
                    )}
                </Box>
                {status && (
                    <InspectionStatusChip status={status} label={statusLabel} />
                )}
                <IconButton
                    size="small"
                    sx={{ color: 'rgba(255,255,255,0.5)' }}
                    onClick={(e) => {
                        e.stopPropagation()
                        setExpanded(!expanded)
                    }}
                >
                    {expanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
            </Box>
            <Collapse in={expanded}>
                <Box sx={{ px: 2, pb: 2, pt: 0 }}>{children}</Box>
            </Collapse>
        </Box>
    )
}

interface InspectionEmptyStateProps {
    message: string
}

export const InspectionEmptyState: React.FC<InspectionEmptyStateProps> = ({
    message,
}) => {
    return (
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            {message}
        </Typography>
    )
}

// Keep legacy export for any external references
export const InspectionSectionCard = LifecycleSection
